import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Col,
  Row,
  Container,
  Dropdown,
  Table,
  DropdownButton,
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEllipsisV, faUndo } from '@fortawesome/pro-solid-svg-icons'
import { useHistory, useParams } from 'react-router'
import { useDispatch } from 'react-redux'
import { CourseLmsProfileType, MonitoringStatus } from 'enum'
import Tag from 'components/ui/tag/Tag'
import Breadcrumb, { BreadcrumbItem } from 'design-system/breadcrumb'
import StateMonitor from 'components/ui/state-monitor/StateMonitor'
import * as fromActions from 'store/actions/participants.action'
import EmptyListMessage from 'components/ui/empty-list-message/EmptyListMessage'
import ConfirmationModal from 'components/ui/modals/confirmation/ConfirmationModal'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import CourseState from 'components/courses/CourseState'
import { ParticipantCourses } from 'models/participantCourse.model'
import { useAccess } from 'contexts/AccessContext'
import styles from './CourseInfo.module.scss'
import PermissionsManagementModal from './permissions-management-modal/PermissionsManagementModal'

interface ICourseInfo {
  data: ParticipantCourses
  setShowPermissionsModal?: (state: boolean) => void
  showPermissionsModal?: boolean
}

interface Params {
  userId: string
}

const CourseInfo: React.FC<ICourseInfo> = ({
  data,
  setShowPermissionsModal,
  showPermissionsModal = false,
}) => {
  const { t } = useTranslation()

  const { userId } = useParams<Params>()
  const history = useHistory()
  const dispatch = useDispatch()
  const [showInfoModal, setShowInfoModal] = useState(false)
  const [showResetModal, setShowResetModal] = useState(0)

  const handleSetAdmin = () => {
    setShowInfoModal(false)
    dispatch({
      type: fromActions.PARTICIPANT_EDIT_ROLE,
      payload: { user: { roleId: null }, userId },
    })
  }

  useEffect(() => {
    dispatch({ type: fromActions.GET_ROLE_BY_USERID, payload: userId })
  }, [dispatch, userId])

  const handleResetCourse = () => {
    dispatch({
      type: fromActions.RESET_COURSE,
      payload: { userId, courseId: showResetModal },
    })
    setShowResetModal(0)
  }

  const { permissions } = useAccess()

  const disableReset = !permissions.canManageCourses
  const disableMonitor = !permissions.canMonitor

  return (
    <Container fluid className={styles.container}>
      <Row className={`mb-3 ${styles.row1}`}>
        <Breadcrumb separator=">">
          <BreadcrumbItem
            href="/configuration/users"
            text={t('configuration.user.breadcrum.init')}
          />
          <BreadcrumbItem text={t('configuration.user.breadcrum.admin')} />
        </Breadcrumb>
      </Row>
      <Row className={`${styles.row2}`}>
        <Col xs={9} className="pl-0 ">
          <h1 className={styles.title}>{t('configuration.user.title')} </h1>
          <h2 className={styles.description}>
            {t('configuration.user.description')}{' '}
          </h2>
          {data.courses && (
            <p className={`m-0 mt-4 ${styles.description}`}>
              {data.courses.length +
                ' ' +
                t(
                  `configuration.user.assignedCourses.${
                    data.courses.length === 1 ? 'singular' : 'plural'
                  }`
                )}{' '}
            </p>
          )}
        </Col>
      </Row>
      <Row
        className={`mt-5 ${
          !data.courses || data.courses.length === 0 ? styles.row3 : ''
        }`}
      >
        {data.courses && data.courses.length > 0 ? (
          <Table responsive className={styles.table}>
            <thead>
              <tr>
                <th>{t('configuration.user.course.name')}</th>
                <th>{t('configuration.user.course.lmsProfile')}</th>
                <th>{t('configuration.user.course.type.title')}</th>
                <th>{t('configuration.user.course.state.title')}</th>
                <th>{t('configuration.user.course.progress.title')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.courses?.map(
                (
                  {
                    id,
                    name,
                    lmsProfiles,
                    lmsProfileType,
                    advance,
                    enrol,
                    state,
                  },
                  index: number
                ) => {
                  return (
                    <tr key={id + name + index}>
                      <td style={{ maxWidth: '30%' }}>
                        {name}
                        {enrol && enrol.attributes.reboots > 0 && (
                          <CommonToolTip
                            text={`${t(
                              'configuration.user.course.rebootsCount'
                            )} ${enrol.attributes.reboots}`}
                            position="top"
                          >
                            <FontAwesomeIcon
                              className={styles.rebootIcon}
                              icon={faUndo}
                            />
                          </CommonToolTip>
                        )}
                      </td>
                      <td>
                        {lmsProfiles?.length ? (
                          lmsProfiles.map((lmsProfile) => (
                            <Tag
                              key={lmsProfile.id}
                              text={lmsProfile.name}
                              color={lmsProfile.color}
                              size="sm"
                            />
                          ))
                        ) : (
                          <Tag
                            key={'empty' + index}
                            text={t(
                              'configuration.user.course.unassignedProfile'
                            )}
                            color={'#343a40'}
                            size="sm"
                          />
                        )}
                      </td>
                      <td>
                        {t(
                          `configuration.user.course.type.${
                            lmsProfileType === CourseLmsProfileType.Optional
                              ? 'optional'
                              : 'required'
                          }`
                        )}
                      </td>
                      <td>
                        <CourseState state={state} />
                      </td>
                      <td>{<StateMonitor state={advance} />}</td>
                      <td>
                        <DropdownButton
                          id="dropdown-menu-align-right"
                          alignRight
                          variant="secondary"
                          className={`${styles.DesignSystemButton} ${styles.secondary}`}
                          title={<FontAwesomeIcon icon={faEllipsisV} />}
                        >
                          <Dropdown.Item
                            className={styles.dropdownItem}
                            disabled={
                              disableMonitor ||
                              !enrol ||
                              advance === MonitoringStatus.Pending
                            }
                            onClick={() =>
                              history.push(
                                `/administration/courses/${id}/monitoring/participant/${data.id}`
                              )
                            }
                          >
                            {t(`configuration.user.seeResults`)}
                          </Dropdown.Item>
                          <Dropdown.Item
                            className={styles.dropdownItem}
                            disabled={
                              disableReset ||
                              !enrol ||
                              advance === MonitoringStatus.Pending
                            }
                            onClick={() => setShowResetModal(id)}
                          >
                            {t(`configuration.user.reset`)}
                          </Dropdown.Item>
                        </DropdownButton>
                      </td>
                    </tr>
                  )
                }
              )}
            </tbody>
          </Table>
        ) : (
          <EmptyListMessage>
            <p className="m-0">{t('configuration.user.withoutCourses')}</p>
          </EmptyListMessage>
        )}
      </Row>
      {/* Manage Permissions */}
      {showPermissionsModal && (
        <PermissionsManagementModal
          show={showPermissionsModal}
          userId={Number(userId)}
          onClose={() => setShowPermissionsModal?.(false)}
        />
      )}
      {/* Remove Permissions */}
      <ConfirmationModal
        show={showInfoModal}
        acceptButtonText={t('configuration.user.resetModal.buttons.accept')}
        text={t(`configuration.user.removePermissionsMessage`)}
        onAccept={handleSetAdmin}
        onClose={() => setShowInfoModal(false)}
      />
      {/* Reset Course */}
      <ConfirmationModal
        show={showResetModal != 0}
        title={t('configuration.user.resetModal.title')}
        acceptButtonText={t('configuration.user.resetModal.buttons.accept')}
        text={t('configuration.user.resetModal.text')}
        onAccept={handleResetCourse}
        onClose={() => setShowResetModal(0)}
      />
    </Container>
  )
}

export default CourseInfo
